import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { toast } from '@/components/ui/helpers/toast';

import { useLogout } from '@/features/auth/hooks/use-logout';
import { getApiErrorMessage } from '@/lib/helpers/api-errors/get-api-error-message';
import * as Logger from '@/lib/monitoring/logger';

interface UseAccountSignOutOptions {
  onBeforeSignOut?: () => void;
}

export function useAccountSignOut(options: UseAccountSignOutOptions = {}) {
  const [isSigningOut, setIsSigningOut] = useState(false);
  const onBeforeSignOutRef = useRef(options.onBeforeSignOut);

  useLayoutEffect(() => {
    onBeforeSignOutRef.current = options.onBeforeSignOut;
  });

  const logout = useLogout({
    onError: error => {
      toast.error(getApiErrorMessage(error), {});
      setIsSigningOut(false);
    },
  });

  const signOut = useCallback(async () => {
    if (isSigningOut) {
      return;
    }

    setIsSigningOut(true);
    onBeforeSignOutRef.current?.();

    try {
      await logout();
    } catch (error) {
      Logger.warn('Account sign out failed', { error });
    }
  }, [isSigningOut, logout]);

  return { signOut, isSigningOut };
}
